import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { blogApi, BlogSettingsApi, useBlogSettings } from "api/shop";
import { useToaster } from "utils";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaModal } from "components/molecules";
import {
  PaziaColorPicker,
  PaziaFileDropzone,
  PaziaFormButton,
  PaziaInput,
  PaziaLoader,
  PaziaTextarea,
} from "components/atoms";
import { useShopImageUpload } from "pages/Shop/Sections/SectionForms/useShopImageUpload";

interface BlogSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FORM_NAMES = {
  TITLE: "title",
  BASE_PATH: "basePath",
  META_DESCRIPTION: "metaDescription",
  HERO_TEXT: "heroText",
  HERO_IMAGE: "heroImage",
  HERO_BACKGROUND_COLOR: "heroBackgroundColor",
} as const;

const BASE_PATH_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const schema = yup
  .object({
    [FORM_NAMES.TITLE]: yup.string().trim().required("Nazwa sekcji jest wymagana"),
    [FORM_NAMES.BASE_PATH]: yup
      .string()
      .trim()
      .required("Adres URL jest wymagany")
      .matches(BASE_PATH_REGEX, "Dozwolone są tylko małe litery, cyfry i myślniki (bez ukośników)"),
    [FORM_NAMES.META_DESCRIPTION]: yup.string().trim(),
    [FORM_NAMES.HERO_TEXT]: yup.string().trim(),
    [FORM_NAMES.HERO_IMAGE]: yup.string(),
    [FORM_NAMES.HERO_BACKGROUND_COLOR]: yup.string(),
  })
  .required();

interface FormValues {
  title: string;
  basePath: string;
  metaDescription: string;
  heroText: string;
  heroImage: string;
  heroBackgroundColor: string;
}

export const BlogSettingsModal = ({ isOpen, onClose }: BlogSettingsModalProps) => {
  const { blogSettings, isLoading, invalidate } = useBlogSettings();
  const { showSuccess, showError } = useToaster();
  const { uploadImage } = useShopImageUpload();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: blogSettings?.title ?? "",
      basePath: blogSettings?.basePath ?? "",
      metaDescription: blogSettings?.metaDescription ?? "",
      heroText: blogSettings?.heroText ?? "",
      heroImage: blogSettings?.heroImage ?? "",
      heroBackgroundColor: blogSettings?.heroBackgroundColor ?? "",
    },
  });
  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid },
  } = methods;
  const heroImage = watch(FORM_NAMES.HERO_IMAGE);
  const heroBackgroundColor = watch(FORM_NAMES.HERO_BACKGROUND_COLOR);

  useEffect(() => {
    if (blogSettings) {
      reset({
        title: blogSettings.title ?? "",
        basePath: blogSettings.basePath ?? "",
        metaDescription: blogSettings.metaDescription ?? "",
        heroText: blogSettings.heroText ?? "",
        heroImage: blogSettings.heroImage ?? "",
        heroBackgroundColor: blogSettings.heroBackgroundColor ?? "",
      });
    }
  }, [blogSettings, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      const trimmedMetaDescription = data.metaDescription.trim();
      const trimmedHeroText = data.heroText.trim();
      const payload: BlogSettingsApi = {
        title: data.title.trim(),
        basePath: data.basePath.trim(),
        metaDescription: trimmedMetaDescription === "" ? undefined : trimmedMetaDescription,
        heroText: trimmedHeroText === "" ? undefined : trimmedHeroText,
        heroImage: data.heroImage === "" ? undefined : data.heroImage,
        heroBackgroundColor: data.heroBackgroundColor === "" ? undefined : data.heroBackgroundColor,
      };
      await blogApi.updateBlogSettings(payload);
      showSuccess("Zapisano ustawienia bloga");
      invalidate();
      onClose();
    } catch (error) {
      showError("Nie udało się zapisać ustawień bloga");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PaziaModal id="blog-settings-modal" onCloseRequest={onClose} isOpen={isOpen} size="2xl">
      {isOpen && (
        <ModalPadding>
          <ModalHeader>Ustawienia bloga</ModalHeader>

          {isLoading ? (
            <PaziaLoader position="center" />
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6">
                <div className="grid gap-1">
                  <PaziaInput name={FORM_NAMES.TITLE} label="Nazwa sekcji" placeholder='np. "Poradnik"' required />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Wyświetlana na stronie klienta jako nagłówek, w breadcrumbach i meta title (np. "Poradnik").
                  </p>
                </div>

                <div className="grid gap-1">
                  <PaziaInput
                    name={FORM_NAMES.BASE_PATH}
                    label="Adres URL (prefiks)"
                    placeholder='np. "poradnik"'
                    required
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Zmiana spowoduje zmianę adresów wszystkich wpisów (np. "poradnik" → /poradnik/tytul-wpisu) — warto
                    skonfigurować przekierowania po zmianie.
                  </p>
                </div>

                <div className="grid gap-1">
                  <PaziaTextarea
                    name={FORM_NAMES.META_DESCRIPTION}
                    label="Meta opis listy wpisów"
                    placeholder="Krótki opis listy wpisów wykorzystywany jako meta description."
                    rows={3}
                  />
                </div>

                <div className="border-t border-slate-200 pt-4 dark:border-slate-700">
                  <h3 className="mb-3 font-semibold text-slate-800 dark:text-slate-100">Hero strony głównej</h3>

                  <div className="grid gap-6">
                    <div className="grid gap-1">
                      <PaziaTextarea
                        name={FORM_NAMES.HERO_TEXT}
                        label="Opis w hero"
                        placeholder="Krótki tekst wyświetlany pod nazwą sekcji."
                        rows={2}
                      />
                    </div>

                    <div className="grid gap-1">
                      <PaziaFileDropzone
                        name={FORM_NAMES.HERO_IMAGE}
                        label="Obrazek tła hero"
                        type="image"
                        single
                        preview="image"
                        onDrop={async (file: File) => {
                          const url = await uploadImage(file);
                          setValue(FORM_NAMES.HERO_IMAGE, url, { shouldDirty: true });
                        }}
                      />
                      {heroImage && (
                        <button
                          type="button"
                          className="justify-self-start text-xs text-red-500 hover:underline"
                          onClick={() => setValue(FORM_NAMES.HERO_IMAGE, "", { shouldDirty: true })}
                        >
                          Usuń obrazek
                        </button>
                      )}
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Tło hero na stronie listy wpisów. Gdy brak — użyty zostanie kolor tła lub domyślne tło.
                      </p>
                    </div>

                    <div className="grid gap-1">
                      <PaziaColorPicker name={FORM_NAMES.HERO_BACKGROUND_COLOR} label="Kolor tła hero" />
                      {heroBackgroundColor && (
                        <button
                          type="button"
                          className="justify-self-start text-xs text-red-500 hover:underline"
                          onClick={() => setValue(FORM_NAMES.HERO_BACKGROUND_COLOR, "", { shouldDirty: true })}
                        >
                          Wyczyść kolor
                        </button>
                      )}
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Używany, gdy nie ustawiono obrazka tła.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <PaziaFormButton disabled={isSaving || !isValid} />
                </div>
              </form>
            </FormProvider>
          )}
        </ModalPadding>
      )}
    </PaziaModal>
  );
};
